// BacktestReturnStats.tsx — Backtest results' "how did this actually perform" section:
// the fund's time-weighted price CAGR over the backtest window (headline "Rendite p.a.
// (Zeitraum)"), the money-weighted IRR of the actual Sparplan cashflows ("Anlage-Rendite
// p.a. (IRR)", when computable), and the rolling-1-year-return curve — all derived from
// the fund's cached /history prices (fetched here, cache-only, no forecast/synthetic
// data) sliced to [usedFrom, usedTo]. Honest by construction: any figure that can't be
// computed from real cached data (too little history, no contributions at all) is simply
// omitted, never a fabricated placeholder — see lib/backtestReturns.ts / lib/irr.ts.
import { useEffect, useState } from 'preact/hooks';
import type { SimulationResult } from '@etf-cockpit/engine';
import { ApiError, getFundHistory } from '../lib/api.js';
import { backtestMoneyWeightedReturn, backtestPriceCagr } from '../lib/backtestReturns.js';
import { rollingAnnualizedReturns, sliceByWindow } from '../lib/fundAnalytics.js';
import type { PricePoint } from '../lib/fundAnalytics.js';
import { formatDateDe, formatPercent } from '../lib/format.js';
import type { BacktestMeta } from '../lib/types.js';
import { RollingReturnChart } from './RollingReturnChart.js';
import { Hint } from './Hint.js';

export interface BacktestReturnStatsProps {
  meta: BacktestMeta;
  result: SimulationResult;
}

export function BacktestReturnStats({ meta, result }: BacktestReturnStatsProps) {
  const [prices, setPrices] = useState<PricePoint[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFundHistory(meta.isin)
      .then((res) => {
        if (cancelled) return;
        setPrices(res.prices);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Unbekannter Fehler beim Laden der Kurshistorie.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meta.isin]);

  if (loading) {
    return (
      <div class="card">
        <h2>Rendite p.a.</h2>
        <div class="empty-state">
          <span class="spinner" /> Wird geladen…
        </div>
      </div>
    );
  }

  if (error || !prices) {
    return (
      <div class="card">
        <h2>Rendite p.a.</h2>
        <div class="banner banner-error" role="alert">
          <strong>Fehler</strong>
          <span>{error ?? 'Kurshistorie konnte nicht geladen werden.'}</span>
        </div>
      </div>
    );
  }

  const priceCagr = backtestPriceCagr(prices, meta.usedFrom, meta.usedTo);
  const irr = backtestMoneyWeightedReturn(result);
  const rolling = rollingAnnualizedReturns(sliceByWindow(prices, meta.usedFrom, meta.usedTo), 1);

  return (
    <div class="card">
      <h2>Rendite p.a.</h2>
      {priceCagr === null && irr === null ? (
        <div class="empty-state">Zu wenig Kurshistorie im Zeitraum für eine Rendite p.a.</div>
      ) : (
        <>
          <div class="summary-grid">
            {priceCagr !== null ? (
              <div class="summary-tile">
                <div class="label">Rendite p.a. (Zeitraum)</div>
                <div class="value">{formatPercent(priceCagr)}</div>
              </div>
            ) : null}
            {irr !== null ? (
              <div class="summary-tile">
                <div class="label">Anlage-Rendite p.a. (IRR)</div>
                <div class="value">{formatPercent(irr)}</div>
              </div>
            ) : null}
          </div>
          <Hint>
            <p class="field-hint">
              „Rendite p.a. (Zeitraum)“: zeitgewichtete annualisierte Kursrendite des Fonds von{' '}
              {formatDateDe(meta.usedFrom)} bis {formatDateDe(meta.usedTo)} (CAGR der Kurse — unabhängig vom
              Sparplan).
              {irr !== null
                ? ' „Anlage-Rendite p.a. (IRR)“: geldgewichtete Rendite der tatsächlichen Ein-/Auszahlungen dieses Sparplans (berücksichtigt WANN eingezahlt wurde).'
                : ''}
            </p>
          </Hint>
        </>
      )}

      <h3>1-Jahres-Rendite (rollierend)</h3>
      <RollingReturnChart series={rolling.series} />
    </div>
  );
}
